/**
 * Which comments a reader has chosen to see translated, per language
 * (spec §6.3). The translation itself persists server-side in
 * `translationCache` on the comment document; this records only the reader's
 * per-comment display choice, which was otherwise lost on refresh.
 *
 * Presence means translated, absence means original. There is deliberately no
 * global "always translate" preference for this to defer to: accepting the
 * offer translates the remaining comments on that question and records them
 * here, so every piece of state is per comment and reversible with the
 * comment's own toggle.
 *
 * localStorage rather than the user document: this is high-churn,
 * comment-scoped display state, and persisting it server-side would mean a
 * Firestore write per toggle plus an ever-growing map of comment IDs on a
 * document read on every auth state change. The trade is that it does not
 * follow a reader across devices.
 *
 * Bounded by MAX_ENTRIES with FIFO eviction so it cannot accumulate across
 * months of sessions.
 */
const STORAGE_KEY = 'kolexchange:translatedComments';
const MAX_ENTRIES = 500;

// Parsed once per page load — the lookup runs for every comment on mount, and
// re-reading localStorage for each would be wasteful. A second tab writing is
// not reconciled; for display state that is a fair trade for the simplicity.
let cachedEntries = null;

/**
 * Entries are plain `commentId:language` keys. Two earlier shapes are
 * migrated rather than discarded so readers keep the toggles they have
 * already set: the original was the same plain keys, and a later revision
 * stored `[key, boolean]` pairs for a global-preference override that no
 * longer exists — a `false` there meant "show the original", which is now
 * simply absence.
 */
function normaliseEntries(parsed) {
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (Array.isArray(entry) && typeof entry[0] === 'string') {
        return entry[1] ? entry[0] : null;
      }
      return null;
    })
    .filter(Boolean);
}

function readEntries() {
  if (cachedEntries) return cachedEntries;
  if (typeof window === 'undefined') return [];
  let parsed = [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    parsed = raw ? JSON.parse(raw) : [];
  } catch (error) {
    parsed = [];
  }
  cachedEntries = normaliseEntries(parsed);
  return cachedEntries;
}

function writeEntries(entries) {
  cachedEntries = entries;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (error) {
    // Quota exceeded, or Safari private mode. Persistence is a convenience,
    // never a reason to break the toggle — the in-memory cache still holds
    // for this page load.
    cachedEntries = entries;
  }
}

const keyFor = (commentId, language) => `${commentId}:${language}`;

export function isTranslated(commentId, language) {
  if (!commentId || !language) return false;
  return readEntries().includes(keyFor(commentId, language));
}

export function rememberTranslated(commentId, language) {
  if (!commentId || !language) return;
  const key = keyFor(commentId, language);
  // Re-append so the entries a reader actually uses survive eviction.
  const entries = readEntries().filter((entry) => entry !== key);
  entries.push(key);
  writeEntries(entries.slice(-MAX_ENTRIES));
}

export function rememberManyTranslated(commentIds, language) {
  if (!language || !commentIds?.length) return;
  const keys = commentIds.map((id) => keyFor(id, language));
  const entries = readEntries().filter((entry) => !keys.includes(entry));
  writeEntries([...entries, ...keys].slice(-MAX_ENTRIES));
}

export function forgetTranslated(commentId, language) {
  if (!commentId || !language) return;
  const key = keyFor(commentId, language);
  writeEntries(readEntries().filter((entry) => entry !== key));
}

/**
 * How the reader last answered the "translate the rest?" offer, or null.
 *
 * `accepted` means later questions offer immediately rather than waiting for
 * three more manual translations — they have already shown this is what they
 * want, and making them re-earn it on every question is the tedium the offer
 * exists to remove. `declined` silences it until they show intent again.
 */
const OFFER_ANSWER_KEY = 'kolexchange:autoTranslateOffer';

export function translateOfferAnswer() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(OFFER_ANSWER_KEY);
  } catch (error) {
    return null;
  }
}

export function setTranslateOfferAnswer(answer) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(OFFER_ANSWER_KEY, answer);
  } catch (error) {
    // Private mode — being asked again next visit is a small cost.
  }
}
